import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { doc, updateDoc, deleteDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { deleteUser } from 'firebase/auth';
import { db, auth } from '../firebase';
import { Icons } from './Icons';
import './SettingsPanel.css';

const PERMISSION_OPTIONS = [
  { key: 'stats', label: 'Stats' },
  { key: 'companies', label: 'Companies' },
  { key: 'roles', label: 'Roles & Status' },
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'liveStatus', label: 'Live Status' },
  { key: 'updateMessage', label: 'Update Messages' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'notes', label: 'Notes' },
];

export default function SettingsPanel({ onClose }) {
  const { currentUser, userProfile, setUserProfile } = useAuth();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  const [copied, setCopied] = useState(false);

  const [publicEnabled, setPublicEnabled] = useState(userProfile?.publicEnabled !== false);
  const [publicPermissions, setPublicPermissions] = useState(
    userProfile?.publicPermissions || { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false }
  );
  const [quickPadsMode, setQuickPadsMode] = useState(userProfile?.quickPadsMode || 'compact');
  const [showNotifications, setShowNotifications] = useState(userProfile?.showNotifications !== false);
  const [highlightEnabled, setHighlightEnabled] = useState(false);
  const [formattingEnabled, setFormattingEnabled] = useState(userProfile?.formattingEnabled === true);
  const [showSearchTip, setShowSearchTip] = useState(userProfile?.showSearchTip !== false);
  const [showDates, setShowDates] = useState(userProfile?.showDates !== false);
  const [showNetworkEmail, setShowNetworkEmail] = useState(userProfile?.showNetworkEmail === true);
  const [showNetworkPhone, setShowNetworkPhone] = useState(userProfile?.showNetworkPhone === true);
  const [networkPhone, setNetworkPhone] = useState(userProfile?.networkPhone || '');
  const [networkEmail, setNetworkEmail] = useState(userProfile?.networkEmail || userProfile?.email || '');
  const [locationMode, setLocationMode] = useState(userProfile?.locationMode || 'usStates');

  // Dynamic networks
  const [networks, setNetworks] = useState(() => {
    if (userProfile?.networks && userProfile.networks.length > 0) {
      return userProfile.networks.map(n => ({
        ...n,
        emails: (n.emails || []).join('\n'),
        permissions: n.permissions || { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false }
      }));
    }
    // Migrate from legacy network1/network2
    const nets = [];
    if (userProfile?.network1) {
      const n = userProfile.network1;
      nets.push({ name: n.name || 'Network 1', emails: (n.emails || []).join('\n'), permissions: n.permissions || { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false }, enabled: n.enabled !== false });
    }
    if (userProfile?.network2) {
      const n = userProfile.network2;
      nets.push({ name: n.name || 'Network 2', emails: (n.emails || []).join('\n'), permissions: n.permissions || { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false }, enabled: n.enabled !== false });
    }
    if (nets.length === 0) {
      nets.push({ name: 'Network 1', emails: '', permissions: { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false }, enabled: true });
    }
    return nets;
  });

  const [accessCode, setAccessCode] = useState(() => {
    const ac = userProfile?.accessCode || {};
    return { code: ac.code || '', permissions: ac.permissions || { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false }, enabled: ac.enabled !== false };
  });

  const shareUrl = `${window.location.origin}/${userProfile?.shareSlug || userProfile?.username}`;

  function togglePublicPerm(key) {
    setPublicPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleNetworkPerm(index, key) {
    setNetworks(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], permissions: { ...updated[index].permissions, [key]: !updated[index].permissions[key] } };
      return updated;
    });
  }

  function updateNetwork(index, field, value) {
    setNetworks(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }

  function addNetwork() {
    setNetworks(prev => [...prev, {
      name: `Network ${prev.length + 1}`,
      emails: '',
      permissions: { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false },
      enabled: true
    }]);
  }

  function removeNetwork(index) {
    setNetworks(prev => prev.filter((_, i) => i !== index));
  }

  function togglePerm(obj, setObj, key) {
    setObj(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: !prev.permissions[key] } }));
  }

  function parseEmails(text) {
    return text.split('\n').map(e => e.trim().toLowerCase()).filter(e => e && e.includes('@'));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const serializedNetworks = networks.map(n => ({
        name: n.name.trim() || 'Network',
        emails: parseEmails(n.emails),
        permissions: n.permissions,
        enabled: n.enabled
      }));

      const data = {
        publicEnabled,
        publicPermissions,
        quickPadsMode,
        showNotifications,
        highlightEnabled: false,
        formattingEnabled,
        showSearchTip,
        showDates,
        showNetworkEmail,
        showNetworkPhone,
        networkPhone: networkPhone.trim(),
        networkEmail: networkEmail.trim(),
        locationMode,
        networks: serializedNetworks,
        // Keep legacy fields for backward compat
        network1: serializedNetworks[0] || { name: 'Network 1', emails: [], permissions: { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false }, enabled: true },
        network2: serializedNetworks[1] || { name: 'Network 2', emails: [], permissions: { stats: true, companies: true, roles: true, state: true, city: true, liveStatus: false, updateMessage: false, contacts: false, notes: false }, enabled: true },
        accessCode: { code: accessCode.code, permissions: accessCode.permissions, enabled: accessCode.enabled }
      };
      await updateDoc(doc(db, 'users', currentUser.uid), data);
      setUserProfile(prev => ({ ...prev, ...data }));
      toast('Settings saved', 'system');
    } catch (err) { toast(err.message, 'error'); }
    setSaving(false);
  }

  function copyShareLink() {
    navigator.clipboard.writeText(shareUrl);
    toast('Share link copied!', 'system');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleDeleteAccount() {
    try {
      const uid = currentUser.uid;
      const user = auth.currentUser;
      const providerData = user.providerData;
      const isGoogle = providerData.some(p => p.providerId === 'google.com');

      if (isGoogle) {
        const { GoogleAuthProvider, reauthenticateWithPopup } = await import('firebase/auth');
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(user, provider);
      } else {
        const pwd = window.prompt('Please enter your password to confirm deletion:');
        if (!pwd) { setDeleteStep(0); return; }
        const { EmailAuthProvider, reauthenticateWithCredential } = await import('firebase/auth');
        const credential = EmailAuthProvider.credential(user.email, pwd);
        await reauthenticateWithCredential(user, credential);
      }

      const batch = writeBatch(db);
      const companiesSnap = await getDocs(query(collection(db, 'companies'), where('userId', '==', uid)));
      companiesSnap.docs.forEach(d => batch.delete(d.ref));
      const rolesSnap = await getDocs(query(collection(db, 'roles'), where('userId', '==', uid)));
      rolesSnap.docs.forEach(d => batch.delete(d.ref));
      const contactsSnap = await getDocs(query(collection(db, 'contacts'), where('userId', '==', uid)));
      contactsSnap.docs.forEach(d => batch.delete(d.ref));
      if (userProfile?.username) batch.delete(doc(db, 'usernames', userProfile.username.toLowerCase()));
      batch.delete(doc(db, 'users', uid));
      await batch.commit();
      await deleteUser(user);
      setDeleteStep(2);
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') { setDeleteStep(0); return; }
      toast('Error deleting account: ' + err.message, 'error');
      setDeleteStep(0);
    }
  }

  if (deleteStep === 2) {
    return (
      <div className="panel-overlay">
        <div className="panel slide-up">
          <div className="delete-farewell">
            <div className="farewell-icon">🌟</div>
            <h2>All the best for your future endeavors</h2>
            <p>Your account and all data have been permanently deleted.</p>
            <p>We hope TrackThrough helped you on your journey.</p>
            <button className="submit-btn" style={{marginTop: 20}} onClick={() => window.location.href = '/'}>Go to Homepage</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel slide-up" onClick={e => e.stopPropagation()}>
        <div className="panel-header">
          <h2>Settings</h2>
          <button className="panel-close" onClick={onClose}><Icons.X size={18} /></button>
        </div>

        <div className="panel-body">
          {/* Profile */}
          <section className="settings-section">
            <h3>Profile</h3>
            <div className="settings-row"><span className="settings-label">Username</span><span className="settings-value">@{userProfile?.username}</span></div>
            <div className="settings-row"><span className="settings-label">Email</span><span className="settings-value">{currentUser?.email}</span></div>
            <div className="settings-row"><span className="settings-label">First Name</span><span className="settings-value">{userProfile?.firstName}</span></div>
          </section>

          {/* Preferences */}
          <section className="settings-section">
            <h3>Preferences</h3>
            <div className="pref-row">
              <div>
                <span className="settings-label">Quick Notes Display</span>
                <p className="settings-desc" style={{marginTop: 2}}>Choose how your notes appear on the dashboard.</p>
              </div>
              <div className="pref-toggle-group">
                <button className={`pref-btn ${quickPadsMode === 'compact' ? 'active' : ''}`} onClick={() => setQuickPadsMode('compact')}>Compact</button>
                <button className={`pref-btn ${quickPadsMode === 'fixed' ? 'active' : ''}`} onClick={() => setQuickPadsMode('fixed')}>Always Visible</button>
              </div>
            </div>
            <div className="pref-row">
              <div>
                <span className="settings-label">Show Notifications</span>
                <p className="settings-desc" style={{marginTop: 2}}>Brief updates when actions are performed.</p>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={showNotifications} onChange={e => setShowNotifications(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="pref-row">
              <div>
                <span className="settings-label">Text Formatting (Quick Notes)</span>
                <p className="settings-desc" style={{marginTop: 2}}>Enable Bold, Italic, and Underline formatting in plain text mode.</p>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={formattingEnabled} onChange={e => setFormattingEnabled(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="pref-row">
              <div>
                <span className="settings-label">Show Search Tip (Public View)</span>
                <p className="settings-desc" style={{marginTop: 2}}>Display a search tip for third-party viewers.</p>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={showSearchTip} onChange={e => setShowSearchTip(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="pref-row">
              <div>
                <span className="settings-label">Show Applied Dates</span>
                <p className="settings-desc" style={{marginTop: 2}}>Show date fields on roles. When off, dates are hidden everywhere including exports.</p>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={showDates} onChange={e => setShowDates(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="pref-row">
              <div>
                <span className="settings-label">Show Email on Status Page</span>
                <p className="settings-desc" style={{marginTop: 2}}>Display your contact email on your status page for anyone who visits.</p>
                {showNetworkEmail && (
                  <input
                    type="email"
                    value={networkEmail}
                    onChange={e => setNetworkEmail(e.target.value)}
                    placeholder="e.g. yourname@email.com"
                    style={{marginTop: 6, padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: '100%', maxWidth: 220}}
                  />
                )}
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={showNetworkEmail} onChange={e => setShowNetworkEmail(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="pref-row">
              <div>
                <span className="settings-label">Show Phone on Status Page</span>
                <p className="settings-desc" style={{marginTop: 2}}>Display your phone number on your status page for anyone who visits.</p>
                {showNetworkPhone && (
                  <input
                    type="text"
                    value={networkPhone}
                    onChange={e => setNetworkPhone(e.target.value)}
                    placeholder="e.g. +1-555-123-4567"
                    style={{marginTop: 6, padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: '100%', maxWidth: 220}}
                  />
                )}
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={showNetworkPhone} onChange={e => setShowNetworkPhone(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="pref-row">
              <div>
                <span className="settings-label">Location Input Mode</span>
                <p className="settings-desc" style={{marginTop: 2}}>US States: Pick from a dropdown. Manual: Type any location worldwide.</p>
              </div>
              <div className="pref-toggle-group">
                <button className={`pref-btn ${locationMode === 'usStates' ? 'active' : ''}`} onClick={() => setLocationMode('usStates')}>US States</button>
                <button className={`pref-btn ${locationMode === 'manual' ? 'active' : ''}`} onClick={() => setLocationMode('manual')}>Manual</button>
              </div>
            </div>
          </section>

          {/* Public Toggle + Permissions */}
          <section className="settings-section">
            <h3>Public Status Page</h3>
            <div className="public-toggle-row">
              <div>
                <span className="settings-label">Allow public viewing</span>
                <p className="settings-desc" style={{marginTop: 2}}>When off, visitors see a friendly pause message.</p>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={publicEnabled} onChange={e => setPublicEnabled(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            {publicEnabled && (
              <>
                <div className="share-link-row" style={{marginTop: 12}}>
                  <input type="text" readOnly value={shareUrl} className="share-link-input" />
                  <button className={`share-copy-btn ${copied ? 'copied' : ''}`} onClick={copyShareLink}>{copied ? <><Icons.Check size={14} /> Copied!</> : <><Icons.Copy size={14} /> Copy</>}</button>
                </div>
                <p className="settings-desc" style={{marginTop: 12}}>Choose what public viewers can see:</p>
                <div className="perm-checkboxes">
                  {PERMISSION_OPTIONS.map(opt => (
                    <label key={opt.key} className={`perm-checkbox-row ${publicPermissions[opt.key] ? 'checked' : ''}`}>
                      <input type="checkbox" checked={publicPermissions[opt.key]} onChange={() => togglePublicPerm(opt.key)} />
                      <span className="perm-checkbox-label">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* Dynamic Networks */}
          <section className="settings-section">
            <h3>Sharing Networks</h3>
            <p className="settings-desc">Create custom networks and choose exactly what each group can see. Name them anything: Career Team, Friends, Mentors, Classmates, Recruiters.</p>

            {networks.map((network, i) => (
              <div key={i} className={`permission-block ${!network.enabled ? 'paused' : ''}`}>
                <div className="network-top-row">
                  <input className="network-name-input" value={network.name}
                    onChange={e => updateNetwork(i, 'name', e.target.value)}
                    placeholder={`Network ${i + 1}`} maxLength={30} />
                  <label className="toggle-switch mini">
                    <input type="checkbox" checked={network.enabled} onChange={e => updateNetwork(i, 'enabled', e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                  {networks.length > 1 && (
                    <button className="network-remove-btn" onClick={() => removeNetwork(i)} title="Remove network">✕</button>
                  )}
                </div>
                {!network.enabled && <div className="paused-label">Paused</div>}
                {network.enabled && (
                  <>
                    <div className="perm-checkboxes">
                      {PERMISSION_OPTIONS.map(opt => (
                        <label key={opt.key} className={`perm-checkbox-row ${network.permissions[opt.key] ? 'checked' : ''}`}>
                          <input type="checkbox" checked={network.permissions[opt.key]} onChange={() => toggleNetworkPerm(i, opt.key)} />
                          <span className="perm-checkbox-label">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <textarea value={network.emails}
                      onChange={e => updateNetwork(i, 'emails', e.target.value)}
                      placeholder={"Add emails (one per line)\nexample@email.com"} rows={3} />
                  </>
                )}
              </div>
            ))}

            <button className="add-network-btn" onClick={addNetwork}>
              <Icons.Plus size={14} /> Add Network
            </button>
          </section>

          {/* Access Code */}
          <section className="settings-section">
            <h3>Access Code</h3>
            <p className="settings-desc">Share a 4-digit code verbally or via DM. No email needed.</p>
            <p className="settings-desc" style={{marginTop: 4, fontSize: 11, color: 'var(--sage)', fontWeight: 600}}>Only users with the access code can export your visible data to Excel for their reference.</p>
            <div className={`permission-block ${!accessCode.enabled ? 'paused' : ''}`}>
              <div className="network-top-row">
                <label className="access-code-label">4-Digit Code</label>
                <label className="toggle-switch mini">
                  <input type="checkbox" checked={accessCode.enabled} onChange={e => setAccessCode(prev => ({ ...prev, enabled: e.target.checked }))} />
                  <span className="toggle-slider" />
                </label>
              </div>
              {!accessCode.enabled && <div className="paused-label">Paused</div>}
              {accessCode.enabled && (
                <>
                  <input className="access-code-input" type="text" value={accessCode.code}
                    onChange={e => setAccessCode(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    placeholder="0000" maxLength={4} inputMode="numeric" />
                  <div className="perm-checkboxes" style={{marginTop: 10}}>
                    {PERMISSION_OPTIONS.map(opt => (
                      <label key={opt.key} className={`perm-checkbox-row ${accessCode.permissions[opt.key] ? 'checked' : ''}`}>
                        <input type="checkbox" checked={accessCode.permissions[opt.key]} onChange={() => togglePerm(accessCode, setAccessCode, opt.key)} />
                        <span className="perm-checkbox-label">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Privacy */}
          <section className="settings-section">
            <h3>Privacy Defaults</h3>
            <div className="privacy-visual">
              <div className="pv-item shareable"><span>📊 Stats</span><span className="pv-tag green-tag">Configurable</span></div>
              <div className="pv-item shareable"><span>🏢 Companies</span><span className="pv-tag green-tag">Configurable</span></div>
              <div className="pv-item shareable"><span>💼 Roles & Status</span><span className="pv-tag green-tag">Configurable</span></div>
              <div className="pv-item shareable"><span>🏛️ State</span><span className="pv-tag green-tag">Configurable</span></div>
              <div className="pv-item shareable"><span>🏙️ City</span><span className="pv-tag green-tag">Configurable</span></div>
              <div className="pv-item shareable"><span>💬 Live Status</span><span className="pv-tag green-tag">Configurable</span></div>
              <div className="pv-item shareable"><span>👤 Contacts</span><span className="pv-tag green-tag">Per Network</span></div>
              <div className="pv-item shareable"><span>📝 Notes</span><span className="pv-tag green-tag">Per Network</span></div>
              <div className="pv-item private-item"><span>📋 Templates</span><span className="pv-tag yellow-tag">Always Private</span></div>
            </div>
          </section>

          {/* Delete Account */}
          <section className="settings-section">
            <h3>Account Management</h3>
            {deleteStep === 0 && (
              <button className="delete-account-btn" onClick={() => setDeleteStep(1)}>Delete My Account</button>
            )}
            {deleteStep === 1 && (
              <div className="delete-confirm">
                <p className="delete-warning">This will permanently delete your account and all data. This action cannot be undone.</p>
                <div className="delete-actions">
                  <button className="delete-cancel-btn" onClick={() => setDeleteStep(0)}>Cancel</button>
                  <button className="delete-final-btn" onClick={handleDeleteAccount}>Yes, Delete Everything</button>
                </div>
              </div>
            )}
          </section>
        </div>

        <div className="panel-footer">
          <button className="panel-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? <span className="loading-spinner" /> : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
